// @flow

import React, { PureComponent } from 'react';
import connect from '@active/awe-ui-core/lib/hoc/connect';
import PersonalProfile from 'components/PersonalProfile/PersonalProfile';
import UmbrellaPortalController from 'components/UmbrellaPortalController/UmbrellaPortalController';
import ResultCardContainer from 'components/ResultCardContainer/ResultCardContainer';
import Row from 'react-aaui/lib/grid/Row';
import Col from 'components/Col/Col';
import injectL10n from 'react-aaui/lib/shared/injectL10n';
import type { L10n, RouteStagePointWithResult } from 'type/type';
import { getLocale } from 'modules/app/selector';
import BibBoard from '@active/awe-ui-core/lib/business/components/BibBoard';
import sortBy from 'lodash/sortBy';
import classNames from 'classnames';
import ShowAt from 'components/Viewport/ShowAt';
import HideAt from 'components/Viewport/HideAt';
import ClaimTool from 'components/ClaimTool/ClaimTool';
import { pickRank } from 'services/rank/rank';
import compose from 'lodash/fp/compose';
import filter from 'lodash/fp/filter';

import {
  getParticipantHeader, isLiveResult, getSplitWithResults, isTriathlonResult,
} from '../duck/selector';
import './ParticipantHeader.scss';
import MediaLinkBoard from './MediaLinkBoard/MediaLinkBoard';
import AverageSpeedBoard from './AverageSpeedBoard/AverageSpeedBoard';
import PenaltyTimeBoard from './PenaltyTimeBoard/PenaltyTimeBoard';
import ParticipantRank from './ParticipantRank/ParticipantRank';
import ParticipantFinalResultBoard from './ParticipantFinalResultBoard/ParticipantFinalResultBoard';
import LastCrossedSplitBoard from './LastCrossedSplitBoard/LastCrossedSplitBoard';

type Props = {
  data: Object,
  l10n: L10n,
  locale: string,
  isLive: boolean,
  results: RouteStagePointWithResult[],
  toggleGallery?: (val: boolean) => void,
  isKioskMode: boolean
}

class ParticipantHeader extends PureComponent<Props> {
  static defaultProps = {
    event: null,
  }

  renderClaimTool = () => {
    const {
      data: { finalResult, person },
      l10n: { formatMessage },
      isKioskMode,
    } = this.props;
    const { lastName, enterprisePersonId } = person || {};
    const { id: resultId, isClaiming, participantId } = finalResult || {};
    const resultHasBeenClaimed = enterprisePersonId && !enterprisePersonId.match(/^NAP_/);
    const claimToolText = formatMessage('global.claim.result');

    const claimToolparams = {
      resultId,
      text: claimToolText,
      isClaiming,
      lastName,
      participantId,
      resultHasBeenClaimed,
      isKioskMode,
    };

    return <ClaimTool {...claimToolparams} />;
  }

  render() {
    const {
      data: {
        bib, finalResult, participatedRoute, person, locale, groupRanks, bibImage, participantCustomFields,
      },
      l10n: { formatMessage },
      results,
      isLive,
      isTriathlon,
      toggleGallery,
    } = this.props;

    const participantLongId = finalResult ? finalResult.participantId : null;
    const filterdResults = results.filter(r => !!r.result && (r.result.missing || r.result.result !== null));
    const lastCrossedSplit = filterdResults && filterdResults.length > 0
      ? sortBy(filterdResults, [ 'stageSequence', 'sequence' ]).reverse()[0].name || '--'
      : '--';

    const {
      firstName, lastName, age, gender, address, team,
    } = person || {};

    const { penaltyTime } = finalResult || {};

    const { url, thumbnail, fontColor } = bibImage || {};

    const getGroupRanks = isTriathlon
      ? compose(
        pickRank,
        filter(rank => !rank.stageId),
      )
      : pickRank;

    const rankData = getGroupRanks(groupRanks);

    const displayBib = bib || '--';

    const bibBoardComponent = (
      <BibBoard
        title={formatMessage('global.participant.bib')}
        bib={displayBib}
        firstName={firstName}
        imageUrl={url}
        thumbnailUrl={thumbnail}
        fontColor={fontColor} />
    );

    return (
      <div className="participant-header">
        <Row gutter={10} fluid>
          <Col span={12} sm={6} md={6} lg={6} xl={3} className="responsive-personal-profile">
            <ShowAt xlAndAbove>
              <PersonalProfile
                layout="vertical"
                firstName={firstName}
                lastName={lastName}
                age={age}
                gender={gender}
                address={address}
                team={team}
                showTeam
                avatar="icon-user-thin"
                locale={locale} />
              <UmbrellaPortalController hide>{this.renderClaimTool()}</UmbrellaPortalController>
            </ShowAt>
            <HideAt xlAndAbove>
              <PersonalProfile
                layout="horizontal"
                firstName={firstName}
                lastName={lastName}
                age={age}
                gender={gender}
                address={address}
                team={team}
                showTeam
                avatar="icon-user-thin"
                locale={locale} />
              <UmbrellaPortalController hide>{this.renderClaimTool()}</UmbrellaPortalController>
            </HideAt>
          </Col>
          <Col span={12} sm={6} md={6} lg={6} xl={3} className="responsive-bib-column">
            <Row fluid>
              <Col span={7} xl={12}>
                <ResultCardContainer
                  className={classNames(
                    isLive ? 'card-container--live' : 'card-container--final',
                    'bib-board-container',
                  )}>
                  {bibBoardComponent}
                </ResultCardContainer>
              </Col>
              <Col span={5} xl={12}>
                {isLive ? (
                  <LastCrossedSplitBoard lastCrossedSplit={lastCrossedSplit} />
                ) : (
                  <MediaLinkBoard participantCustomFields={participantCustomFields} toggleGallery={toggleGallery} />
                )}
              </Col>
            </Row>
          </Col>
          <Col span={12} md={12} xl={6}>
            <Row gutter={10} fluid>
              <Col className="column-final" span={12} lg={6} sm={6}>
                <ParticipantFinalResultBoard
                  finalResult={finalResult}
                  participatedRoute={participatedRoute}
                  participantLongId={participantLongId} />
              </Col>
              {isTriathlon ? (
                <Col span={12} lg={6} md={6} sm={6} className="column-penalty-time">
                  <PenaltyTimeBoard formatMessage={formatMessage} penaltyTime={penaltyTime} />
                  <HideAt xlAndAbove>
                    <Col span={12}>
                      <ParticipantRank rankData={rankData} dataFont={20} shouldDisplayTotal={false} />
                    </Col>
                  </HideAt>
                </Col>
              ) : (
                <Col span={12} lg={6} md={6} sm={6} className="column-average">
                  <AverageSpeedBoard formatMessage={formatMessage} finalResult={finalResult} />
                  <HideAt xlAndAbove>
                    <Col span={12} className="participant-header__rank">
                      <ParticipantRank rankData={rankData} dataFont={20} shouldDisplayTotal={false} />
                    </Col>
                  </HideAt>
                </Col>
              )}
            </Row>
            <ShowAt xlAndAbove>
              <Col span={12} className="participant-header__rank">
                <ParticipantRank rankData={rankData} dataFont={32} />
              </Col>
            </ShowAt>
          </Col>
        </Row>
      </div>
    );
  }
}

const mapState = (state) => {
  return {
    data: getParticipantHeader(state),
    locale: getLocale(state),
    isLive: isLiveResult(state),
    results: getSplitWithResults(state),
    isTriathlon: isTriathlonResult(state),
  };
};

export default injectL10n()(connect(mapState)(ParticipantHeader));
